import React, { useState, useRef } from "react";
import {
  Button,
  Col,
  message,
  Modal,
  Row,
  Skeleton,
  Switch,
  Table,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";

import { useFetchConstant } from "apis/constant";
import { patchAdviseeCashOutflow } from "apis/advisee";

import InfoCard from "components/InfoCard/InfoCard";
import SectionContainer from "components/SectionContainer/SectionContainer";
import Title from "components/Title/Title";

import { constructDisplayPrice, getLabelOfConstant } from "utils/general";

import AddExpensesModal from "./components/AddExpensesModal/AddExpensesModal";
import OverrideTotalModal from "../OverrideTotalModal/OverrideTotalModal";
import {
  OverridingLabel,
  OverridingRow,
  SummaryValueText,
} from "./CashOutflows.styles";
import EditableTable from "./components/EditableTable/EditableTable";
import Locale from "react-intl-universal";

const { confirm } = Modal;

const tipsDescription = (
  <span>
    By overriding the <b>Total Expenses</b>, all the calculations will be based
    on the overrided value. You can <b>switch back to auto-calculate</b> to be
    based on the expenses added.
  </span>
);

const SummaryCashOutflowInfoCard = ({
  title,
  totalValue,
  isWithTooltip = false,
}) => {
  return (
    <InfoCard
      title={title}
      tipsDescription={
        isWithTooltip
          ? Locale.get("cashFlowSummary.cashOutflowSummary.tipsDescription")
          : //  tipsDescription
            ""
      }
    >
      <SummaryValueText>
        {constructDisplayPrice(totalValue || 0, false)}
      </SummaryValueText>
    </InfoCard>
  );
};

const formatExpensesForTable = (expenses = []) =>
  expenses.map((payment) => ({
    ...payment,
    self: payment.amount && payment.amount.self,
    spouse: payment.amount && payment.amount.spouse,
  }));

// const getExpensesTableColumns = (types) => {
//   return [
//     {
//       title: "Expenses",
//       dataIndex: "type",
//       key: "type",
//       render: (value, record) => (
//         <span>
//           {getLabelOfConstant(value, types) || "-"}
//           {record.label ? ` - ${record.label}` : ""}
//         </span>
//       ),
//     },
//     {
//       title: "Self",
//       dataIndex: "self",
//       key: "self",
//       render: (value) => (
//         <span>{value ? constructDisplayPrice(value, false) : "-"}</span>
//       ),
//     },
//     {
//       title: "Spouse",
//       dataIndex: "spouse",
//       key: "spouse",
//       render: (value) => (
//         <span>{value ? constructDisplayPrice(value, false) : "-"}</span>
//       ),
//     },
//     {
//       title: "Total",
//       render: (value, record) => {
//         const selfAmt = Number(record.self) || 0;
//         const spouseAmt = Number(record.spouse) || 0;
//         const total = selfAmt + spouseAmt;
//         return <span>{total ? constructDisplayPrice(total, false) : "-"}</span>;
//       },
//     },
//   ];
// };

const CashOutflows = ({ expenses, cashOutflowSummary, expensesTemplate }) => {
  const { id: adviseeId } = useParams();
  const [showAddNewCashOutflowModal, setShowAddNewCashOutflowModal] = useState(
    false
  );
  const [showOverridingTotalModal, setShowOverridingTotalModal] = useState(
    false
  );
  const {
    isLoading: isLoanTypesLoading,
    selection: loanTypes,
  } = useFetchConstant("loanTypes");
  const {
    isLoading: isFixedPaymentTypesLoading,
    selection: fixedPaymentTypes,
  } = useFetchConstant("fixedPaymentTypes");
  const {
    isLoading: isVariablePaymentTypesLoading,
    selection: variablePaymentTypes,
  } = useFetchConstant("variablePaymentTypes");
  const isConstantsLoading =
    isLoanTypesLoading ||
    isFixedPaymentTypesLoading ||
    isVariablePaymentTypesLoading;
  // const outflowPaymentTypes = [
  //   ...loanTypes,
  //   ...fixedPaymentTypes,
  //   ...variablePaymentTypes
  // ];
  const [selectedPaymentIndex, setSelectedSourceIndex] = useState(-1);

  const handleOnOverrideTotalSwitchChange = (checked) => {
    if (checked) {
      setShowOverridingTotalModal(checked);
    } else {
      confirm({
        title: Locale.get("cashFlowSummary.cashOutflowSummary.confirmMsg"),
        content: Locale.get(
          "cashFlowSummary.cashOutflowSummary.confirmMsgContent"
        ),
        okText: Locale.get("cashFlowSummary.yes"),
        onOk() {
          const payload = {
            overridingTotal: {
              self: 0,
              spouse: 0,
            },
          };
          patchAdviseeCashOutflow(adviseeId, payload)
            .then((res) => {
              message.success(Locale.get("cashFlowSummary.updateSuccess"));
            })
            .catch((ex) => {
              message.error(Locale.get("cashFlowSummary.errMsgCashflow"));
            });
        },
        onCancel() {},
      });
    }
  };

  const updateCashOutflowExpenses = (
    expenses,
    successMessage = Locale.get("cashFlowSummary.updateSuccess")
  ) => {
    const loanPayments = [];
    const variablePayments = [];
    const fixedPayments = [];
    expenses.forEach((expense) => {
      if (expense.category === "loanPayments") {
        loanPayments.push(expense);
      } else if (expense.category === "variablePayments") {
        variablePayments.push(expense);
      } else if (expense.category === "fixedPayments") {
        fixedPayments.push(expense);
      }
    });
    patchAdviseeCashOutflow(adviseeId, {
      loanPayments,
      variablePayments,
      fixedPayments,
    })
      .then((res) => {
        message.success(successMessage);
        setShowAddNewCashOutflowModal(false);
        setSelectedSourceIndex(-1);
      })
      .catch((ex) => {
        message.error(Locale.get("cashFlowSummary.errMsgCashflow"));
      });
  };

  const handleOnOverridingTotalModalOkButtonClick = (values) => {
    patchAdviseeCashOutflow(adviseeId, values)
      .then((res) => {
        message.success(Locale.get("cashFlowSummary.updateSuccess"));
        setShowOverridingTotalModal(false);
      })
      .catch((ex) => {
        message.error(Locale.get("cashFlowSummary.errMsgCashflow"));
      });
  };

  const handleOnExpensesRowSelect = (index) => {
    setSelectedSourceIndex(index);
    setShowAddNewCashOutflowModal(true);
  };

  const handleOnAddExpensesModalOkButtonClick = (values) => {
    if (selectedPaymentIndex > -1) {
      let editedExpenses = [...expenses];
      editedExpenses[selectedPaymentIndex] = values;
      updateCashOutflowExpenses(editedExpenses);
      setSelectedSourceIndex(-1);
    } else {
      const newExpenses = [...expenses, values];
      updateCashOutflowExpenses(
        newExpenses,
        Locale.get("cashFlowSummary.newExpenseAdded")
      );
    }
  };

  const handleOnAddExpensesModalRemoveButtonClick = () => {
    const newExpenses = [...expenses];
    newExpenses.splice(selectedPaymentIndex, 1);
    updateCashOutflowExpenses(
      newExpenses,
      Locale.get("cashFlowSummary.removeExpense")
    );
  };

  const handleOnAddExpensesModalCancelButtonClick = () => {
    setSelectedSourceIndex(-1);
    setShowAddNewCashOutflowModal(false);
  };

  return (
    <>
      <Row justify="space-between" align="middle">
        <Title>
          {Locale.get("cashFlowSummary.cashOutflowSummary.cashOutflow")}
        </Title>

        {/* <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setShowAddNewCashOutflowModal(true)}
        >
          Add new expenses
        </Button> */}
      </Row>
      {isConstantsLoading ? (
        <Skeleton active />
      ) : (
        <>
          <SectionContainer>
            <OverridingRow align="middle">
              <OverridingLabel>
                {Locale.get("cashFlowSummary.cashOutflowSummary.overrideLabel")}
              </OverridingLabel>
              <Switch
                checked={cashOutflowSummary.isOverridden}
                onChange={handleOnOverrideTotalSwitchChange}
                style={{ marginLeft: "8px" }}
                size="small"
              />
            </OverridingRow>
            <Row gutter={[16, 16]}>
              <Col span={12} xl={8}>
                <SummaryCashOutflowInfoCard
                  title={Locale.get(
                    "cashFlowSummary.cashOutflowSummary.tWithoutLoanPremium"
                  )}
                  totalValue={cashOutflowSummary.totalExpensesValue}
                  isWithTooltip
                />
              </Col>
              <Col span={12} xl={8}>
                <SummaryCashOutflowInfoCard
                  title={Locale.get(
                    "cashFlowSummary.cashOutflowSummary.tLoanInstallment"
                  )}
                  totalValue={cashOutflowSummary.totalLoanInstalmentsValue}
                />
              </Col>
              <Col span={12} xl={8}>
                <SummaryCashOutflowInfoCard
                  title={Locale.get(
                    "cashFlowSummary.cashOutflowSummary.tMonthlyPremium"
                  )}
                  totalValue={cashOutflowSummary.monthlyPremium}
                />
              </Col>
            </Row>
          </SectionContainer>
          {/* <Table
            columns={getExpensesTableColumns(outflowPaymentTypes)}
            dataSource={formatExpensesForTable(expenses)}
            onRow={(record, index) => {
              return {
                onClick: () => handleOnExpensesRowSelect(index)
              };
            }}
            pagination={false}
            rowKey={record => record._id}
          /> */}
          <EditableTable
            data={formatExpensesForTable(expenses)}
            expensesTemplate={expensesTemplate}
          />
        </>
      )}
      {showAddNewCashOutflowModal && (
        <AddExpensesModal
          defaultValues={
            selectedPaymentIndex > -1 && expenses[selectedPaymentIndex]
          }
          isLoading={isConstantsLoading}
          onOk={handleOnAddExpensesModalOkButtonClick}
          onClose={handleOnAddExpensesModalCancelButtonClick}
          onRemove={handleOnAddExpensesModalRemoveButtonClick}
          loanTypes={loanTypes}
          fixedPaymentTypes={fixedPaymentTypes}
          variablePaymentTypes={variablePaymentTypes}
          visible={showAddNewCashOutflowModal}
        />
      )}
      {showOverridingTotalModal && (
        <OverrideTotalModal
          defaultValues={cashOutflowSummary}
          onOk={handleOnOverridingTotalModalOkButtonClick}
          onCancel={() => setShowOverridingTotalModal(false)}
          title="Override Total Amount of Expenses (excluding Loans and Premiums)"
          visible={showOverridingTotalModal}
        />
      )}
    </>
  );
};

export default CashOutflows;
